import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { resolvePageKeyFromPathname } from "../analytics/pageRegistry";
import type { ProductBaseProperties, ProductProfileType } from "../analytics/product";
import type { PageRegistryEntry, ProductArea } from "../analytics/pageRegistry";
import { useCachedMedium } from "./useCachedMedium";
import { useCachedTerreiro } from "./useCachedTerreiro";
import { useSignupAnalyticsContext } from "./useSignupAnalyticsContext";

export type ProductAnalyticsContext = ReturnType<
  typeof useSignupAnalyticsContext
> & {
  activeProfileType: ProductProfileType;
  terreiroId?: string;
  mediumId?: string;
};

export const useProductAnalyticsContext = (): ProductAnalyticsContext => {
  const signupContext = useSignupAnalyticsContext();
  const cachedTerreiro = useCachedTerreiro();
  const cachedMedium = useCachedMedium();

  return useMemo(() => {
    const terreiroId = cachedTerreiro?.id;
    const mediumId = cachedMedium?.id;
    const activeProfileType: ProductProfileType = terreiroId
      ? "terreiro"
      : "personal";

    return {
      ...signupContext,
      activeProfileType,
      terreiroId,
      mediumId,
    };
  }, [cachedMedium?.id, cachedTerreiro?.id, signupContext]);
};

export const buildProductBaseFromRegistry = (
  registryEntry: PageRegistryEntry,
  context: ProductAnalyticsContext,
  location: ReturnType<typeof useLocation>,
): ProductBaseProperties => ({
  pageKey: registryEntry.pageKey,
  routePath: location.pathname,
  sourcePage: registryEntry.sourcePage,
  productArea: registryEntry.productArea,
  activeProfileType: context.activeProfileType,
  terreiroId: context.terreiroId,
  mediumId: context.mediumId,
});

type ProductPageBaseFallback = {
  pageKey: string;
  sourcePage: string;
  productArea: ProductArea;
};

export const useProductPageBase = (
  fallback: ProductPageBaseFallback,
): ProductBaseProperties => {
  const location = useLocation();
  const context = useProductAnalyticsContext();

  return useMemo(() => {
    const registryEntry = resolvePageKeyFromPathname(location.pathname);
    if (registryEntry) {
      return buildProductBaseFromRegistry(registryEntry, context, location);
    }

    return {
      pageKey: fallback.pageKey,
      routePath: location.pathname,
      sourcePage: fallback.sourcePage,
      productArea: fallback.productArea,
      activeProfileType: context.activeProfileType,
      terreiroId: context.terreiroId,
      mediumId: context.mediumId,
    };
  }, [
    context,
    fallback.pageKey,
    fallback.productArea,
    fallback.sourcePage,
    location,
  ]);
};

export const useProductAnalyticsBase = (): ProductBaseProperties => {
  const location = useLocation();
  const context = useProductAnalyticsContext();

  return useMemo(() => {
    const registryEntry = resolvePageKeyFromPathname(location.pathname);
    if (registryEntry) {
      return buildProductBaseFromRegistry(registryEntry, context, location);
    }

    return {
      pageKey: "unknown",
      routePath: location.pathname,
      sourcePage: "Unknown",
      activeProfileType: context.activeProfileType,
      terreiroId: context.terreiroId,
      mediumId: context.mediumId,
    };
  }, [context, location]);
};
